import { useEffect } from "react";
import { useLoaderData } from "react-router-dom";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/AuthStore";
import { useBookingsCalendarStore } from "@/store/BookingsCalendarStore";
import { ApiService } from "@/api/services/ApiService";
import { filterDatesBySelection, getCalendarDates, getTodaysBookings } from "@/lib/helpers";
import { IBooking, IBookingCalendar } from "@/lib/types";
import BookingDayCalendar from "./BookingDayCalendar";

// ---------------------- API

const bookingsQuery = (bookableResourceId: string) => ({
  queryKey: ["bookings-calendar", bookableResourceId] as const,
  queryFn: async () => ApiService.bookings.getBookingsCalendar(bookableResourceId!),
});

export const loader = (queryClient: QueryClient) => async () => {
  const bookableResourceId = useAuthStore.getState().userResourceId;

  // userResourceId may still be null when this loader runs in parallel with protectedLoader.
  // Return empty array so the component renders and then fetches once Zustand is populated.
  if (!bookableResourceId) return [] as IBooking[];

  return queryClient.ensureQueryData<IBooking[]>(bookingsQuery(bookableResourceId));
};

// ---------------------- Component

const BookingsListCalendar = () => {
  // --------- Variables / Hooks

  const initialData = useLoaderData() as IBooking[];

  const { userResourceId } = useAuthStore();

  const { setRefetchFunction, selectedFilter } = useBookingsCalendarStore();

  const { data: bookings = [], refetch } = useQuery({
    ...bookingsQuery(userResourceId!),
    initialData,
    enabled: !!userResourceId,
  });

  // Get calendar dates and apply filter
  const allCalendarDates = getCalendarDates();

  const filteredCalendarDates = filterDatesBySelection(allCalendarDates, selectedFilter);

  // Filter out dates with no bookings
  const datesWithBookings = filteredCalendarDates.filter((dateInfo) => {
    const todaysBookings = getTodaysBookings(dateInfo.fullDate, bookings ?? []);

    return todaysBookings.length > 0;
  });

  // --------- Effects

  useEffect(() => {
    setRefetchFunction(refetch);
  }, []);

  // --------- JSX

  if (datesWithBookings.length === 0) {
    return (
      <p className="mt-6 text-sm text-gray-700 dark:text-gray-300 xs:text-base sm:text-lg">
        No bookings in the selected range
      </p>
    );
  }

  return (
    <>
      <ul className="flex w-full flex-col items-start justify-start gap-2">
        {datesWithBookings.map((dateInfo, index) => {
          const todaysBookings = getTodaysBookings(
            dateInfo.fullDate,
            bookings ?? [],
          ) as IBookingCalendar[];
          const previousDate = index > 0 ? datesWithBookings[index - 1] : null;
          const showMonthHeader = !previousDate || previousDate.month !== dateInfo.month;

          return (
            <li
              key={dateInfo.fullDate.toDateString()}
              className="w-full"
            >
              {/*******************************************/}
              {/* Month Header */}
              {/*******************************************/}

              {showMonthHeader && (
                <p className="my-2 mt-4 text-base font-bold text-gray-700 dark:text-gray-300 xs:text-base sm:text-lg">
                  {dateInfo.month}
                </p>
              )}

              {/*******************************************/}
              {/* Booking */}
              {/*******************************************/}

              <BookingDayCalendar
                day={dateInfo.dayOfWeek}
                dayOfMonth={dateInfo.dayOfMonth}
                date={dateInfo.fullDate}
                bookings={todaysBookings}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default BookingsListCalendar;
